import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import classnames from "classnames";
import { useDebounce } from "../../hooks/index.js";
import { Icon } from "../../Icon/index.js";
import { Label, Preloader } from "../common/index.js";
import { useAutocompleteReducer } from "../hooks/index.js";
import { TextFieldMasked } from "../TextFieldMasked/index.js";
import SuggestedInput_styles from "./SuggestedInput.styles.js";
import { ListBox } from "./ListBox.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function SuggestedInput_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else SuggestedInput_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
const DEBOUNCE_TIMEOUT = 200;
const SuggestedInput = (props)=>{
    var _useDebounce;
    const { id, name, label, labelIcon, value, onChange, onBlur, placeholder, disabled = false, required = false, clearable = false, loading = false, tooltip, error = false, helperText, renderOption, limit = 50, minInputValueLength = 1, mask } = props;
    const { classes } = SuggestedInput_styles();
    const reactId = useId();
    const inputId = useMemo(()=>null != id ? id : reactId, [
        id,
        reactId
    ]);
    const { items, status, hasMore, requestItems, resetItems } = useAutocompleteReducer('load' === props.type ? props.loadItems : void 0);
    const [isLoading, setLoading] = useState(false);
    const [filter, setFilter] = useState({
        limit,
        offset: 0,
        query: ''
    });
    const [open, setOpen] = useState(false);
    const isItemsLoading = isLoading || 'loading' === status;
    const debouncedInputValue = null != (_useDebounce = useDebounce(value, DEBOUNCE_TIMEOUT)) ? _useDebounce : '';
    const handleChange = useCallback((_, nextValue)=>{
        onChange(null != nextValue ? nextValue : void 0);
        if ('load' === props.type) {
            resetItems();
            setFilter(_object_spread_props(_object_spread({}, filter), {
                offset: 0,
                query: ''
            }));
        }
        setOpen(false);
    }, [
        onChange,
        filter,
        props.type,
        resetItems
    ]);
    const handleInputChange = useCallback((_, nextValue, reason)=>{
        if ('reset' === reason) return;
        onChange(null != nextValue ? nextValue : void 0);
        if (null == nextValue ? void 0 : nextValue.replace(/_+/g, '').trim()) {
            if (!open) setOpen(true);
        } else setOpen(false);
    }, [
        onChange,
        open
    ]);
    const handleBlur = useCallback(()=>{
        null == onBlur || onBlur();
        if (open) setOpen(false);
    }, [
        onBlur,
        open
    ]);
    const handleOpen = useCallback(()=>{
        if (null == value ? void 0 : value.replace(/_+/g, '').trim()) setOpen(true);
    }, [
        value
    ]);
    const requestNextItems = ()=>{
        null == requestItems || requestItems(filter, 'get');
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: filter.offset + filter.limit
        }));
    };
    useEffect(()=>{
        if ('local' === props.type || !open || '' !== debouncedInputValue && (null == debouncedInputValue ? void 0 : debouncedInputValue.length) < minInputValueLength) return;
        const abortRequest = null == requestItems ? void 0 : requestItems(_object_spread_props(_object_spread({}, filter), {
            offset: 0,
            query: debouncedInputValue
        }), 'search');
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: filter.limit
        }));
        setLoading(false);
        return ()=>{
            null == abortRequest || abortRequest();
        };
    }, [
        open,
        debouncedInputValue
    ]);
    const filterOptions = (optionList, state)=>{
        const cleanInput = state.inputValue.replace(/_/g, '').trim();
        return optionList.filter((optionElement)=>optionElement.toLowerCase().includes(cleanInput.toLowerCase()));
    };
    return /*#__PURE__*/ jsxs(FormControl, {
        className: classnames(classes.suggestedInput, 'aqa_suggested_input'),
        children: [
            label && /*#__PURE__*/ jsx(Label, {
                label: label,
                required: required,
                tooltip: tooltip,
                icon: labelIcon,
                inputId: inputId
            }),
            /*#__PURE__*/ jsx(Autocomplete, {
                classes: {
                    paper: classes.paper,
                    option: classes.option
                },
                options: 'load' === props.type ? items : props.externalItems,
                filterOptions: filterOptions,
                fullWidth: true,
                freeSolo: true,
                renderTags: ()=>null,
                disabled: disabled || loading,
                disableClearable: !clearable,
                clearIcon: /*#__PURE__*/ jsx(Icon, {
                    name: "dismiss_substract",
                    color: "stroke.secondary",
                    size: "24"
                }),
                inputValue: loading ? 'Загрузка...' : value,
                onInputChange: mask ? void 0 : handleInputChange,
                onChange: handleChange,
                open: open,
                onOpen: handleOpen,
                onClose: handleBlur,
                renderOption: (renderOptionProps, option)=>/*#__PURE__*/ jsx("li", _object_spread_props(_object_spread({}, renderOptionProps), {
                        children: renderOption ? renderOption(option) : /*#__PURE__*/ jsx("div", {
                            children: option
                        })
                    })),
                slotProps: {
                    listbox: _object_spread({
                        component: ListBox
                    }, {
                        dataLength: items.length,
                        next: requestNextItems,
                        hasMore,
                        loading: isItemsLoading,
                        long: !!renderOption
                    })
                },
                renderInput: (params)=>mask ? /*#__PURE__*/ jsx(TextFieldMasked, _object_spread_props(_object_spread({}, params), {
                        id: inputId,
                        mask: mask,
                        name: name,
                        error: error,
                        helperText: helperText,
                        placeholder: placeholder || label,
                        loading: loading,
                        classes: {
                            root: classes.inputRoot
                        },
                        onChange: handleInputChange
                    })) : /*#__PURE__*/ jsx(TextField, _object_spread(_object_spread_props(_object_spread({}, params), {
                        id: inputId,
                        name: name,
                        classes: {
                            root: classes.inputRoot
                        },
                        autoComplete: "off",
                        variant: "outlined",
                        fullWidth: true,
                        error: error,
                        helperText: helperText,
                        placeholder: placeholder || label
                    }), loading ? {
                        slotProps: {
                            input: {
                                endAdornment: /*#__PURE__*/ jsx(Preloader, {})
                            }
                        }
                    } : {}))
            })
        ]
    });
};
export { SuggestedInput };
